// Airline logo utility functions
import airlineIconsData from '../data/Airline_Icons.json';
import { buildAssetUrl } from '../config/config.js';
import { hasLogoRegistry, getLogoVersion } from '../services/airlineLogoRegistry.js';

/**
 * Get airline logo URL by airline code
 * Logos managed on the admin Airlines page (database) are used once the board has
 * received that list; until then the logos bundled in Airline_Icons.json are used.
 * @param {string} airlineCode - IATA airline code (e.g., 'AF', 'DL', 'P4')
 * @returns {string|null} - Logo URL or null if not found
 */
export const getAirlineLogoUrl = (airlineCode) => {
  if (!airlineCode) return null;

  if (hasLogoRegistry()) {
    const code = airlineCode.toUpperCase();
    const version = getLogoVersion(code);
    return version ? buildAssetUrl(`/api/airlines/logo/${encodeURIComponent(code)}?v=${version}`) : null;
  }

  const airline = airlineIconsData.find(
    item => item.airlineCode === airlineCode.toUpperCase()
  );

  if (!airline?.URL) return null;

  // Handle relative URLs for local assets in Electron
  if (airline.URL.startsWith('/')) {
    // In Electron, we need to use the file:// protocol for local assets
    const isElectron = window.location.protocol === 'file:';
    if (isElectron) {
      // Use the current directory for local assets
      return `.${airline.URL}`;
    }
    return airline.URL;
  }

  return airline.URL;
};

/**
 * Get airline logo image element with fallback
 * @param {string} airlineCode - IATA airline code
 * @param {Object} props - Image props (height, width, className, etc.)
 * @returns {JSX.Element} - Image element with error handling
 */
export const AirlineLogo = ({ airlineCode, height = "60px", width = "60px", className = "", ...props }) => {
  const logoUrl = getAirlineLogoUrl(airlineCode);

  if (!logoUrl) {
    // No logo available - return null so parent can handle styling
    return null;
  }

  return (
    <img
      src={logoUrl}
      alt={`${airlineCode} airline logo`}
      className={`airline-logo fade-in ${className}`}
      style={{
        height: height,
        width: 'auto',
        maxWidth: '100%',
        objectFit: 'contain',
        display: 'block',
        margin: '0 auto'
      }}
      loading="eager"
      decoding="sync"
      onError={(e) => {
        // Hide failed image - parent will show no-logo styling
        e.target.style.display = 'none';
      }}
      {...props}
    />
  );
};

export default {
  getAirlineLogoUrl,
  AirlineLogo
};